import React, { useCallback, useEffect, useMemo, useState } from "react";
import { C, roleAtLeast } from "../constants.js";
import { apiFetch, fetchStageDashboard } from "../api.js";
import { SopBodyRenderer } from "../components/SopBodyRenderer.jsx";

// Substep 5-η.4: matter stages, mirrors STAGES in app/services/stages.py.
// Same treatment as the STAGES constant in FirmSopsPage — hardcoded until
// stages ever go per-firm (unlikely for MCB / Clio-standard firms).
const STAGES = [
  { key: "new_case",    label: "New Case" },
  { key: "treatment",   label: "Treatment" },
  { key: "demand",      label: "Demand" },
  { key: "mediation",   label: "Mediation" },
  { key: "litigation",  label: "Litigation" },
  { key: "settlement",  label: "Settlement" },
];
const STAGE_LABEL_BY_KEY = Object.fromEntries(STAGES.map(s => [s.key, s.label]));

/**
 * TasksCard — workflow tasks + inline SOP reference on a matter
 * (Phase 2 substep 2-δ + Substep 5-η.2 merge with the retired
 * StageSopsCard).
 *
 * Shows every Task row for the matter, grouped by the source template
 * name (ad-hoc tasks get their own "Other tasks" group). Substep 5-η.2
 * merges what StageSopsCard used to show separately:
 *   - Inline Ask (top of card) — matter-context boosted retrieval
 *   - Each task with a linked SOP shows the SOP body inline when expanded
 *   - Firm-admin+ can link/change the SOP for any task from a compact
 *     dropdown; writes cascade to the source TemplateTask so every
 *     future matter using that template inherits the link
 *   - Cross-cutting firm handbook (SOPs with no stage tags) at the
 *     bottom in a collapsible footer
 *
 * Body text renders as <pre> (task) or monospace <div> (SOP body) —
 * markdown rendering is a later polish.
 */
export function TasksCard({ matterId, showNotification, authStatus, bare = false, onTaskChange }) {
  const role = authStatus?.firm_user?.role || "pending";
  const canLinkSop = roleAtLeast(role, "firm_admin");

  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addTitle, setAddTitle] = useState("");
  const [addBody, setAddBody] = useState("");
  const [pendingId, setPendingId] = useState(null);  // task being toggled/deleted
  const [applying, setApplying] = useState(false);

  // Substep 5-η.2: SOP support ---------------------------------------
  const [allSops, setAllSops] = useState([]);          // [{id, title, category, applicable_stages}]
  const [stageLabel, setStageLabel] = useState(null);  // for handbook header + template stage picker
  const [stageKey, setStageKey] = useState(null);      // for task-stage bucketing (5-η.4)
  const [sopBodyCache, setSopBodyCache] = useState({});// {sopId: {title, category, body}}
  const [linkingTaskId, setLinkingTaskId] = useState(null); // task with SOP-picker open
  const [linkingQuery, setLinkingQuery] = useState("");
  // Substep 5-η.5.d: inline Ask retired from this card per Aaron —
  // paralegals ask via the Firm SOPs page directly. Ask-your-SOPs
  // endpoint still exists; the matter-context boost path via matter_id
  // is intact (no consumer today; can re-add UI on demand later).
  const [handbookCollapsed, setHandbookCollapsed] = useState(true);
  // Substep 5-η.4: stage-bucketed grouping + template-stage admin picker.
  const [otherStagesCollapsed, setOtherStagesCollapsed] = useState(true);
  const [stagePickerTemplateId, setStagePickerTemplateId] = useState(null);
  const [stagePickerDraft, setStagePickerDraft] = useState([]);
  const [stagePickerSaving, setStagePickerSaving] = useState(false);
  // Substep 5-η.5.e: hide Apply template + Add task behind a "..." menu
  // so they don't invite miss-clicks; they're rare actions after 5-η.4's
  // auto-apply-on-stage-transition ships.
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // ─── Loaders ─────────────────────────────────────────────────────
  const loadTasks = useCallback(async () => {
    try {
      const d = await apiFetch(`/api/matters/${matterId}/workflow-tasks`);
      setTasks(d.data || []);
    } catch (e) {
      console.error("workflow-tasks load failed:", e);
      showNotification?.(`Failed to load tasks: ${e.message}`, "error");
    }
  }, [matterId, showNotification]);

  const loadTemplates = useCallback(async () => {
    try {
      const d = await apiFetch(`/api/matter-templates`);
      setTemplates(d.data || []);
    } catch (e) {
      console.error("matter-templates load failed:", e);
    }
  }, []);

  const loadSops = useCallback(async () => {
    try {
      const d = await apiFetch(`/api/firm-sops`);
      setAllSops(d.data || []);
    } catch (e) {
      console.error("firm-sops load failed:", e);
    }
  }, []);

  const loadStage = useCallback(async () => {
    try {
      const d = await fetchStageDashboard(matterId);
      setStageLabel(d.current_stage_label || null);
      setStageKey(d.current_stage || null);
    } catch (e) {
      // Non-fatal — Ask still works, bucketing falls back to "all in this stage".
    }
  }, [matterId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadTasks(), loadTemplates(), loadSops(), loadStage()]);
      setLoading(false);
    })();
  }, [loadTasks, loadTemplates, loadSops, loadStage]);

  // Substep 5-η.4: two-level grouping — outer bucket by stage relevance,
  // inner grouping by source template.
  //
  // Outer buckets (matches Aaron's ask "tasks and stages should go hand
  // and hand"):
  //   thisStage    — task's source template applies to current stage OR
  //                  is cross-cutting (empty applicable_stages)
  //   otherStages  — task's source template applies to stages != current
  //   adhoc        — task has no source template (created via + Add task)
  //
  // Inner grouping (unchanged from before): tasks group by
  // source_matter_template_name within each bucket, ad-hoc under one
  // "Other tasks" heading.
  //
  // Each inner group carries meta so the render can show the template's
  // applicable_stages chip + admin edit button on the group header:
  //   { templateId: int|null, templateName: str, applicableStages: [str],
  //     tasks: [Task] }
  const bucketed = useMemo(() => {
    const thisStage = new Map();
    const otherStages = new Map();
    const adhocKey = "__adhoc__";

    for (const t of tasks) {
      const tmplId = t.source_matter_template_id;
      const tmplName = t.source_matter_template_name;
      const tmplStages = t.source_matter_template_stages || [];

      let bucket;
      let key;
      if (!tmplId) {
        // Ad-hoc — show under a dedicated third section.
        bucket = null;      // handled separately below
        key = adhocKey;
      } else if (!tmplStages.length || (stageKey && tmplStages.includes(stageKey))) {
        // Applies to this stage OR is cross-cutting.
        bucket = thisStage;
        key = `${tmplId}:${tmplName || `template ${tmplId}`}`;
      } else {
        bucket = otherStages;
        key = `${tmplId}:${tmplName || `template ${tmplId}`}`;
      }

      const target = bucket || null;
      if (target) {
        if (!target.has(key)) {
          target.set(key, {
            templateId: tmplId,
            templateName: tmplName || `Template #${tmplId}`,
            applicableStages: tmplStages,
            tasks: [],
          });
        }
        target.get(key).tasks.push(t);
      }
    }

    // Ad-hoc: build separately as a single flat group.
    const adhocTasks = tasks.filter(t => !t.source_matter_template_id);
    const adhoc = adhocTasks.length
      ? { templateId: null, templateName: "Other tasks",
          applicableStages: [], tasks: adhocTasks }
      : null;

    const sortInner = (list) => list.sort(
      (a, b) => (a.sort_order - b.sort_order) || (a.id - b.id));
    for (const grp of thisStage.values()) sortInner(grp.tasks);
    for (const grp of otherStages.values()) sortInner(grp.tasks);
    if (adhoc) sortInner(adhoc.tasks);

    const byName = (a, b) => a.templateName.localeCompare(b.templateName);
    return {
      thisStage: [...thisStage.values()].sort(byName),
      otherStages: [...otherStages.values()].sort(byName),
      adhoc,
    };
  }, [tasks, stageKey]);

  // Cross-cutting SOPs — no applicable_stages tags → visible everywhere
  // as firm-wide reference material (General Rules, Save Docs, etc.).
  const crossCuttingSops = useMemo(
    () => allSops.filter(s => !s.applicable_stages || s.applicable_stages.length === 0),
    [allSops],
  );

  // ─── Action handlers ─────────────────────────────────────────────
  const applyTemplate = async (templateId, templateName) => {
    setApplying(true);
    setShowTemplatePicker(false);
    try {
      const resp = await apiFetch(
        `/api/matters/${matterId}/apply-template/${templateId}`,
        { method: "POST", body: JSON.stringify({}) },
      );
      showNotification?.(
        `Applied "${templateName}" — ${resp.tasks_created} tasks added.`,
        "success",
      );
      await loadTasks();
    } catch (e) {
      if (e.status === 409) {
        if (window.confirm(
          `"${templateName}" is already applied to this matter. Apply again (creates duplicates)?`
        )) {
          try {
            const resp = await apiFetch(
              `/api/matters/${matterId}/apply-template/${templateId}`,
              { method: "POST", body: JSON.stringify({ force: true }) },
            );
            showNotification?.(
              `Applied "${templateName}" again — ${resp.tasks_created} more tasks added.`,
              "success",
            );
            await loadTasks();
          } catch (e2) {
            showNotification?.(`Apply failed: ${e2.message}`, "error");
          }
        }
      } else {
        showNotification?.(`Apply failed: ${e.message}`, "error");
      }
    }
    setApplying(false);
  };

  const createAdhocTask = async () => {
    if (!addTitle.trim()) return;
    try {
      await apiFetch(`/api/matters/${matterId}/workflow-tasks`, {
        method: "POST",
        body: JSON.stringify({
          title: addTitle.trim(),
          body: addBody.trim() || null,
        }),
      });
      setAddTitle("");
      setAddBody("");
      setShowAddForm(false);
      await loadTasks();
      showNotification?.("Task added.", "success");
    } catch (e) {
      showNotification?.(`Add task failed: ${e.message}`, "error");
    }
  };

  const toggleComplete = async (task) => {
    // Substep 5-η.5.f: optimistic update. Old flow awaited PATCH then
    // loadTasks — two sequential round trips ~400-800ms before the
    // checkbox visually moved. Now the UI flips immediately; PATCH
    // happens in the background; onTaskChange fires so StageTracker
    // can refresh its progress bar in the same beat. On error we
    // revert AND surface the failure.
    const newStatus = task.status === "completed" ? "pending" : "completed";
    const prevStatus = task.status;
    setTasks(prev => prev.map(t =>
      t.id === task.id ? { ...t, status: newStatus } : t,
    ));
    onTaskChange?.();
    try {
      await apiFetch(`/api/workflow-tasks/${task.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
    } catch (e) {
      // Revert local state so what the user sees matches the server.
      setTasks(prev => prev.map(t =>
        t.id === task.id ? { ...t, status: prevStatus } : t,
      ));
      onTaskChange?.();
      showNotification?.(`Update failed: ${e.message}`, "error");
    }
  };

  const deleteTask = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    setPendingId(task.id);
    try {
      await apiFetch(`/api/workflow-tasks/${task.id}`, { method: "DELETE" });
      await loadTasks();
      showNotification?.("Task deleted.", "success");
    } catch (e) {
      if (e.status === 403) {
        showNotification?.("Only attorneys and above can delete tasks.", "warning");
      } else {
        showNotification?.(`Delete failed: ${e.message}`, "error");
      }
    }
    setPendingId(null);
  };

  // Substep 5-η.2: fetch full SOP body on-demand, cache so re-expansion
  // is instant. Never blocks the row — spinner shows briefly on first expand.
  const fetchSopBody = useCallback(async (sopId) => {
    if (sopBodyCache[sopId]) return;
    try {
      const full = await apiFetch(`/api/firm-sops/${sopId}`);
      setSopBodyCache(prev => ({ ...prev, [sopId]: full }));
    } catch (e) {
      showNotification?.(`Failed to load SOP: ${e.message}`, "error");
    }
  }, [sopBodyCache, showNotification]);

  const linkSopToTask = async (taskId, sopId) => {
    // Substep 5-η.6.c: optimistic update, no loadTasks() refetch.
    // Old flow set pendingId + awaited PATCH + awaited loadTasks —
    // the loadTasks setTasks reset caused the whole task section to
    // re-render, which Aaron perceived as the section "disappearing
    // and coming back." Now the SOP chip on the specific task updates
    // in place; everything else stays untouched.
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    const prevSopId = task.sop_id;
    const prevSopTitle = task.sop_title;
    const newSopTitle = sopId === null
      ? null
      : (allSops.find(s => s.id === sopId)?.title || null);

    setTasks(prev => prev.map(t =>
      t.id === taskId ? { ...t, sop_id: sopId, sop_title: newSopTitle } : t,
    ));
    setLinkingTaskId(null);
    setLinkingQuery("");

    try {
      // sop_id = 0 clears (per backend sentinel); passing null means "leave alone" per PATCH semantics.
      const payload = sopId === null ? { sop_id: 0 } : { sop_id: sopId };
      await apiFetch(`/api/workflow-tasks/${taskId}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      showNotification?.(
        sopId === null
          ? "SOP unlinked from task and template."
          : "SOP linked to task and cascaded to template.",
        "success",
      );
    } catch (e) {
      // Revert on error so what the user sees matches the server.
      setTasks(prev => prev.map(t =>
        t.id === taskId ? { ...t, sop_id: prevSopId, sop_title: prevSopTitle } : t,
      ));
      showNotification?.(`Link failed: ${e.message}`, "error");
    }
  };

  // Substep 5-η.4: template-stage picker handlers.
  const openStagePicker = (templateId, currentStages) => {
    setStagePickerTemplateId(templateId);
    setStagePickerDraft([...(currentStages || [])]);
  };
  const closeStagePicker = () => {
    setStagePickerTemplateId(null);
    setStagePickerDraft([]);
  };
  const toggleStageInPicker = (key) => {
    setStagePickerDraft(prev => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key); else s.add(key);
      return [...s];
    });
  };
  const saveStagePicker = async () => {
    // Substep 5-η.6.d: optimistic template-stage save. Old flow awaited
    // Promise.all([loadTasks(), loadTemplates()]) after PATCH, which
    // reset both state slices wholesale and caused the whole task
    // section to re-render — Aaron's "disappears and comes back" flash.
    //
    // Fix: rewrite the relevant slices of tasks + templates in place.
    // Every task whose source template is the one we just edited gets
    // its source_matter_template_stages updated locally, which pushes
    // bucketed to re-slot them (This Stage vs Other Stages) without a
    // network round trip. Snapshot both slices for the error revert.
    if (stagePickerTemplateId == null) return;
    const templateId = stagePickerTemplateId;
    const nextStages = [...stagePickerDraft];
    const prevTasks = tasks;
    const prevTemplates = templates;

    setTasks(prev => prev.map(t =>
      t.source_matter_template_id === templateId
        ? { ...t, source_matter_template_stages: nextStages }
        : t,
    ));
    setTemplates(prev => prev.map(t =>
      t.id === templateId
        ? { ...t, applicable_stages: nextStages }
        : t,
    ));
    closeStagePicker();
    setStagePickerSaving(true);
    try {
      await apiFetch(`/api/matter-templates/${templateId}`, {
        method: "PATCH",
        body: JSON.stringify({ applicable_stages: nextStages }),
      });
      showNotification?.(
        nextStages.length
          ? `Template stages saved (${nextStages.length}/${STAGES.length}).`
          : "Template set to cross-cutting (applies to all stages).",
        "success",
      );
    } catch (e) {
      // Revert both slices so what the user sees matches the server.
      setTasks(prevTasks);
      setTemplates(prevTemplates);
      showNotification?.(`Save failed: ${e.message}`, "error");
    }
    setStagePickerSaving(false);
  };

  if (loading) return null;

  // Substep 5-η.5.d: bare=true drops background/border/marginBottom
  // so this content shares a card with StageTracker. Padding aligned
  // with StageTracker's bare padding so both halves line up visually.
  const cardStyle = bare
    ? { padding: "12px 24px 20px" }
    : { background: "#fff", borderRadius: "12px", padding: "20px 24px",
        border: `1px solid ${C.warmGray}`, marginBottom: "20px" };

  return React.createElement("div", { style: cardStyle },
    // ─── Card header ──────────────────────────────────────────────
    React.createElement("div", {
      style: {
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: "12px", gap: "8px", flexWrap: "wrap",
      },
    },
      React.createElement("h2", {
        style: {
          fontSize: "13px", color: C.textMuted, textTransform: "uppercase",
          letterSpacing: "0.08em", fontWeight: 600, margin: 0,
        },
      }, `Workflow Tasks${tasks.length ? ` (${tasks.length})` : ""}`),

      // Substep 5-η.5.e: Apply template + Add task hidden behind "..."
      // — semi-rare after auto-apply-on-stage-transition ships. Menu
      // closes on any action or when the user clicks the "..." again.
      React.createElement("div", { style: { position: "relative" } },
        React.createElement("button", {
          onClick: () => setShowActionsMenu(v => !v),
          "aria-label": "More task actions",
          title: "More task actions",
          style: {
            width: "32px", height: "32px", borderRadius: "8px",
            border: `1px solid ${C.warmGray}`, background: "#fff",
            color: C.textMuted, fontSize: "18px", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            lineHeight: 1,
          },
        }, "⋯"),
        showActionsMenu && React.createElement("div", {
          style: {
            position: "absolute", top: "calc(100% + 4px)", right: 0,
            background: "#fff", border: `1px solid ${C.warmGray}`,
            borderRadius: "8px", padding: "4px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            display: "flex", flexDirection: "column", gap: "2px",
            minWidth: "160px", zIndex: 10,
          },
        },
          React.createElement("button", {
            onClick: () => {
              setShowTemplatePicker(v => !v);
              setShowAddForm(false);
              setShowActionsMenu(false);
            },
            disabled: applying,
            style: {
              padding: "8px 12px", textAlign: "left", border: "none",
              background: "transparent", color: C.textDark,
              fontSize: "12px", cursor: "pointer", borderRadius: "6px",
            },
            onMouseEnter: e => e.currentTarget.style.background = C.warmWhite,
            onMouseLeave: e => e.currentTarget.style.background = "transparent",
          }, applying ? "Applying…" : "Apply template"),
          React.createElement("button", {
            onClick: () => {
              setShowAddForm(v => !v);
              setShowTemplatePicker(false);
              setShowActionsMenu(false);
            },
            style: {
              padding: "8px 12px", textAlign: "left", border: "none",
              background: "transparent", color: C.textDark,
              fontSize: "12px", cursor: "pointer", borderRadius: "6px",
            },
            onMouseEnter: e => e.currentTarget.style.background = C.warmWhite,
            onMouseLeave: e => e.currentTarget.style.background = "transparent",
          }, "+ Add task"),
        ),
      ),
    ),

    // Substep 5-η.5.d: Ask input removed from this card — Aaron uses
    // the Firm SOPs page directly for SOP Q&A. Backend endpoint still
    // supports matter_id for stage-boosted retrieval when we want it.

    // ─── Template picker (conditional) ────────────────────────────
    showTemplatePicker && templates.length > 0 && React.createElement("div", {
      style: {
        padding: "12px", background: C.warmWhite, borderRadius: "8px",
        border: `1px solid ${C.warmGray}`, marginBottom: "16px",
      },
    },
      React.createElement("div", {
        style: { fontSize: "12px", color: C.textMuted, marginBottom: "8px", fontWeight: 600 },
      }, "Pick a template to apply to this matter:"),
      React.createElement("div", { style: { display: "flex", flexDirection: "column", gap: "4px" } },
        ...templates.map(t =>
          React.createElement("button", {
            key: t.id,
            onClick: () => applyTemplate(t.id, t.name),
            style: {
              padding: "8px 12px", textAlign: "left", border: `1px solid ${C.warmGray}`,
              background: "#fff", borderRadius: "6px", cursor: "pointer",
              fontSize: "13px", color: C.navy,
              display: "flex", justifyContent: "space-between", alignItems: "center",
            },
          },
            React.createElement("span", { style: { fontWeight: 500 } }, t.name),
            React.createElement("span", { style: { fontSize: "11px", color: C.textMuted } },
              `${t.task_count} tasks${t.practice_area ? ` · ${t.practice_area}` : ""}`,
            ),
          )
        ),
      ),
    ),

    // ─── Add-task inline form (conditional) ───────────────────────
    showAddForm && React.createElement("div", {
      style: {
        padding: "12px", background: C.warmWhite, borderRadius: "8px",
        border: `1px solid ${C.warmGray}`, marginBottom: "16px",
      },
    },
      React.createElement("input", {
        type: "text",
        placeholder: "Task title",
        value: addTitle,
        onChange: (e) => setAddTitle(e.target.value),
        style: {
          width: "100%", padding: "8px 10px", marginBottom: "8px",
          border: `1px solid ${C.warmGray}`, borderRadius: "6px",
          fontSize: "13px", boxSizing: "border-box",
        },
      }),
      React.createElement("textarea", {
        placeholder: "Notes / instructions (optional)",
        value: addBody,
        onChange: (e) => setAddBody(e.target.value),
        rows: 3,
        style: {
          width: "100%", padding: "8px 10px", marginBottom: "8px",
          border: `1px solid ${C.warmGray}`, borderRadius: "6px",
          fontSize: "13px", boxSizing: "border-box", resize: "vertical",
          fontFamily: "inherit",
        },
      }),
      React.createElement("div", { style: { display: "flex", gap: "8px", justifyContent: "flex-end" } },
        React.createElement("button", {
          onClick: () => { setShowAddForm(false); setAddTitle(""); setAddBody(""); },
          style: {
            padding: "6px 12px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
            background: "#fff", color: C.textMuted, fontSize: "12px", cursor: "pointer",
          },
        }, "Cancel"),
        React.createElement("button", {
          onClick: createAdhocTask,
          disabled: !addTitle.trim(),
          style: {
            padding: "6px 14px", borderRadius: "6px", border: "none",
            background: addTitle.trim() ? C.gold : C.warmGray, color: "#fff",
            fontSize: "12px", fontWeight: 600, cursor: addTitle.trim() ? "pointer" : "default",
          },
        }, "Add"),
      ),
    ),

    // ─── Empty state ──────────────────────────────────────────────
    tasks.length === 0 && !showTemplatePicker && !showAddForm && React.createElement("div", {
      style: {
        padding: "20px", textAlign: "center", color: C.textMuted,
        fontSize: "13px",
      },
    }, "No tasks yet. Apply a template above to get started, or add an individual task."),

    // ─── Substep 5-η.4: Bucketed task list ────────────────────────
    // "For this stage" — always expanded. Empty state ("No tasks for
    // this stage — auto-apply hasn't run yet or no template covers
    // this stage") when there's nothing.
    stageKey && React.createElement("div", { style: { marginBottom: "16px" } },
      React.createElement("div", {
        style: {
          fontSize: "12px", color: C.navy, fontWeight: 700,
          padding: "8px 0 6px 0", borderBottom: `2px solid ${C.gold}`,
          marginBottom: "8px",
          display: "flex", alignItems: "center", gap: "8px",
        },
      },
        React.createElement("span", null,
          `For ${stageLabel || STAGE_LABEL_BY_KEY[stageKey] || stageKey}`),
        React.createElement("span", {
          style: { color: C.textMuted, fontWeight: 400, fontSize: "11px" }
        }, `· ${bucketed.thisStage.reduce((n, g) => n + g.tasks.length, 0)} tasks`),
      ),
      bucketed.thisStage.length === 0
        ? React.createElement("div", {
            style: {
              padding: "14px", fontSize: "12px", color: C.textMuted,
              textAlign: "center", background: C.warmWhite, borderRadius: "6px",
            }
          }, "No tasks for this stage yet. When the matter transitions stages, matching templates auto-apply. Firm admin can tag templates to stages using the button on any template header below.")
        : bucketed.thisStage.map(group =>
            renderTemplateGroup(group, { thisStage: true, stageKey, canLinkSop,
              allSops, expandedId, pendingId, linkingTaskId, linkingQuery,
              sopBodyCache, stagePickerTemplateId, stagePickerDraft,
              stagePickerSaving, toggleComplete, deleteTask, setExpandedId,
              fetchSopBody, setLinkingTaskId, setLinkingQuery, linkSopToTask,
              openStagePicker, closeStagePicker, toggleStageInPicker,
              saveStagePicker, matterId, showNotification })
          ),
    ),

    // "Other stages" — collapsed by default so completed off-stage
    // tasks and future-stage previews don't crowd the current view,
    // but stay reachable for audit trail + reference.
    bucketed.otherStages.length > 0 && React.createElement("div", {
      style: { marginBottom: "16px" }
    },
      React.createElement("button", {
        onClick: () => setOtherStagesCollapsed(v => !v),
        style: {
          width: "100%", textAlign: "left", padding: "6px 0",
          background: "transparent", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", gap: "8px",
          fontSize: "11px", fontWeight: 600, color: C.textMuted,
          letterSpacing: "0.06em", textTransform: "uppercase",
          borderBottom: `1px solid ${C.warmGray}`, marginBottom: "6px",
        }
      },
        React.createElement("span", {
          style: { fontSize: "10px", opacity: 0.7, width: "10px" }
        }, otherStagesCollapsed ? "▸" : "▾"),
        `Other stages (${bucketed.otherStages.reduce((n, g) => n + g.tasks.length, 0)})`,
        React.createElement("span", {
          style: { color: C.textLight, fontWeight: 400, textTransform: "none",
                   letterSpacing: 0, fontSize: "10px" }
        }, "· previous / future stage tasks"),
      ),
      !otherStagesCollapsed && bucketed.otherStages.map(group =>
        renderTemplateGroup(group, { thisStage: false, stageKey, canLinkSop,
          allSops, expandedId, pendingId, linkingTaskId, linkingQuery,
          sopBodyCache, stagePickerTemplateId, stagePickerDraft,
          stagePickerSaving, toggleComplete, deleteTask, setExpandedId,
          fetchSopBody, setLinkingTaskId, setLinkingQuery, linkSopToTask,
          openStagePicker, closeStagePicker, toggleStageInPicker,
          saveStagePicker, matterId, showNotification })
      ),
    ),

    // Ad-hoc tasks — separate section, no stage bucketing possible.
    bucketed.adhoc && React.createElement("div", { style: { marginBottom: "16px" } },
      React.createElement("div", {
        style: {
          fontSize: "11px", color: C.textMuted, textTransform: "uppercase",
          letterSpacing: "0.06em", fontWeight: 600,
          padding: "6px 0 4px 0", borderBottom: `1px solid ${C.warmGray}`,
          marginBottom: "6px",
        },
      }, `Other tasks (${bucketed.adhoc.tasks.length})`),
      ...bucketed.adhoc.tasks.map(task => renderTask(task, {
        expanded: expandedId === task.id,
        pending: pendingId === task.id,
        canLinkSop,
        allSops,
        sopBody: task.sop_id ? sopBodyCache[task.sop_id] : null,
        isLinking: linkingTaskId === task.id,
        linkingQuery,
        onToggle: () => toggleComplete(task),
        onExpand: () => {
          const newExpanded = expandedId === task.id ? null : task.id;
          setExpandedId(newExpanded);
          if (newExpanded && task.sop_id) fetchSopBody(task.sop_id);
        },
        onDelete: () => deleteTask(task),
        onOpenLink: () => { setLinkingTaskId(task.id); setLinkingQuery(""); },
        onCancelLink: () => { setLinkingTaskId(null); setLinkingQuery(""); },
        onLinkQueryChange: setLinkingQuery,
        onPickSop: (sopId) => linkSopToTask(task.id, sopId),
        onUnlink: () => linkSopToTask(task.id, null),
        matterId, showNotification,
      })),
    ),

    // ─── Substep 5-η.2: Firm handbook (cross-cutting SOPs) ────────
    crossCuttingSops.length > 0 && React.createElement("div", {
      style: { marginTop: "16px", borderTop: `1px solid ${C.warmGray}`, paddingTop: "12px" }
    },
      React.createElement("button", {
        onClick: () => setHandbookCollapsed(v => !v),
        style: {
          width: "100%", textAlign: "left", padding: "6px 0",
          background: "transparent", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", gap: "8px",
          fontSize: "11px", fontWeight: 600, color: C.textMuted,
          letterSpacing: "0.06em", textTransform: "uppercase",
        }
      },
        React.createElement("span", {
          style: { fontSize: "10px", opacity: 0.7, width: "10px" }
        }, handbookCollapsed ? "▸" : "▾"),
        `Firm handbook (${crossCuttingSops.length})`,
        React.createElement("span", {
          style: { color: C.textLight, fontWeight: 400, textTransform: "none",
                   letterSpacing: 0, fontSize: "10px" }
        }, "· firm-wide rules, not stage-specific"),
      ),
      !handbookCollapsed && React.createElement("div", {
        style: { display: "flex", flexDirection: "column", gap: "1px",
                 marginTop: "6px", border: `1px solid ${C.warmGray}`,
                 borderRadius: "6px", overflow: "hidden" }
      },
        ...crossCuttingSops.map((sop, i) => React.createElement("div", {
          key: sop.id,
          onClick: () => {
            fetchSopBody(sop.id);
            setExpandedId("sop:" + sop.id);
          },
          style: {
            padding: "8px 12px", cursor: "pointer", background: "#fff",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}`,
            fontSize: "12px",
          },
          onMouseEnter: (e) => e.currentTarget.style.background = C.warmWhite,
          onMouseLeave: (e) => e.currentTarget.style.background = "#fff",
        },
          React.createElement("span", { style: { color: C.navy, fontWeight: 500 } }, sop.title),
          sop.category && React.createElement("span", {
            style: { fontSize: "10px", color: C.textMuted, fontWeight: 600,
                     padding: "1px 6px", borderRadius: "999px", background: C.warmWhite }
          }, sop.category),
        )),
      ),
      // A cross-cutting SOP opened via Firm Handbook (or Ask citation)
      // renders its body below the section rather than in a modal so we
      // stay inside the matter page.
      typeof expandedId === "string" && expandedId.startsWith("sop:") &&
        (() => {
          const sopId = parseInt(expandedId.slice(4), 10);
          const body = sopBodyCache[sopId];
          if (!body) {
            return React.createElement("div", {
              style: { padding: "12px", color: C.textMuted, fontSize: "12px", textAlign: "center" }
            }, "Loading SOP…");
          }
          return React.createElement("div", {
            style: {
              margin: "8px 0 0 0", padding: "14px",
              background: C.warmWhite, borderRadius: "6px",
              border: `1px solid ${C.warmGray}`,
            }
          },
            React.createElement("div", {
              style: {
                display: "flex", justifyContent: "space-between", alignItems: "center",
                marginBottom: "8px", gap: "10px",
              }
            },
              React.createElement("div", { style: { fontWeight: 600, color: C.navy, fontSize: "13px" } }, body.title),
              React.createElement("button", {
                onClick: () => setExpandedId(null),
                style: {
                  border: "none", background: "transparent", color: C.textMuted,
                  cursor: "pointer", fontSize: "16px",
                }
              }, "×"),
            ),
            React.createElement("div", {
              style: { maxHeight: "400px", overflowY: "auto" }
            },
              React.createElement(SopBodyRenderer, {
                body: body.body || "",
                matterId,
                showNotification,
              }),
            ),
          );
        })(),
    ),
  );
}


// Substep 5-η.4: render one template's tasks under a header that
// shows the template's stages (chips) and an admin "assign to stages"
// affordance (firm_admin+ only).
function renderTemplateGroup(group, ctx) {
  const {
    thisStage, stageKey, canLinkSop,
    allSops, expandedId, pendingId, linkingTaskId, linkingQuery,
    sopBodyCache, stagePickerTemplateId, stagePickerDraft, stagePickerSaving,
    toggleComplete, deleteTask, setExpandedId,
    fetchSopBody, setLinkingTaskId, setLinkingQuery, linkSopToTask,
    openStagePicker, closeStagePicker, toggleStageInPicker, saveStagePicker,
    matterId, showNotification,
  } = ctx;

  const stages = group.applicableStages || [];
  const isCrossCutting = stages.length === 0;
  const isPickerOpen = stagePickerTemplateId === group.templateId
                       && group.templateId != null;

  return React.createElement("div", {
    key: `${group.templateId ?? "adhoc"}:${group.templateName}`,
    style: { marginBottom: "12px" },
  },
    React.createElement("div", {
      style: {
        display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap",
        padding: "4px 0 4px 0", marginBottom: "4px",
      }
    },
      React.createElement("div", {
        style: { fontSize: "12px", color: C.textDark, fontWeight: 600 }
      }, group.templateName),
      // Stage chips
      isCrossCutting
        ? React.createElement("span", {
            style: {
              fontSize: "10px", padding: "2px 8px", borderRadius: "999px",
              background: `${C.navy}12`, color: C.navy, fontWeight: 600,
            },
            title: "This template applies to all stages",
          }, "cross-cutting")
        : stages.map(k => React.createElement("span", {
            key: "st-" + k,
            style: {
              fontSize: "10px", padding: "2px 8px", borderRadius: "999px",
              background: `${C.gold}22`, color: C.textDark, fontWeight: 600,
            },
            title: "Matter stage: " + (STAGE_LABEL_BY_KEY[k] || k),
          }, STAGE_LABEL_BY_KEY[k] || k)),
      // Admin edit affordance
      canLinkSop && group.templateId != null && React.createElement("button", {
        onClick: () => openStagePicker(group.templateId, stages),
        style: {
          border: `1px dashed ${C.warmGray}`, background: "transparent",
          color: C.textMuted, cursor: "pointer",
          padding: "1px 6px", borderRadius: "4px", fontSize: "10px",
        },
        title: "Change which matter stages this template applies to (cascades to every future matter)",
      }, "Edit stages"),
    ),

    // Inline stage picker (admin only)
    isPickerOpen && React.createElement("div", {
      style: {
        padding: "10px 12px", background: C.warmWhite, borderRadius: "6px",
        border: `1px solid ${C.warmGray}`, marginBottom: "8px",
      }
    },
      React.createElement("div", {
        style: { fontSize: "11px", color: C.textMuted, marginBottom: "6px" }
      }, "Pick which stages this template applies to. Empty = cross-cutting."),
      React.createElement("div", {
        style: { display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }
      },
        ...STAGES.map(s => {
          const on = stagePickerDraft.includes(s.key);
          return React.createElement("label", {
            key: s.key,
            style: {
              display: "flex", alignItems: "center", gap: "5px",
              padding: "3px 8px", borderRadius: "999px", cursor: "pointer",
              background: on ? C.navy : "#fff",
              color: on ? "#fff" : C.textDark,
              border: `1px solid ${on ? C.navy : C.warmGray}`,
              fontSize: "12px", userSelect: "none",
            }
          },
            React.createElement("input", {
              type: "checkbox", checked: on,
              onChange: () => toggleStageInPicker(s.key),
              style: { margin: 0, cursor: "pointer" },
            }),
            s.label,
          );
        }),
      ),
      React.createElement("div", { style: { display: "flex", gap: "6px", justifyContent: "flex-end" } },
        React.createElement("button", {
          onClick: closeStagePicker,
          disabled: stagePickerSaving,
          style: {
            padding: "5px 12px", borderRadius: "6px",
            border: `1px solid ${C.warmGray}`, background: "#fff",
            color: C.textMuted, fontSize: "11px", cursor: "pointer",
          },
        }, "Cancel"),
        React.createElement("button", {
          onClick: saveStagePicker,
          disabled: stagePickerSaving,
          style: {
            padding: "5px 14px", borderRadius: "6px", border: "none",
            background: stagePickerSaving ? C.warmGray : C.gold, color: "#fff",
            fontSize: "11px", fontWeight: 600,
            cursor: stagePickerSaving ? "default" : "pointer",
          },
        }, stagePickerSaving ? "Saving…" : "Save"),
      ),
    ),

    // Task rows
    ...group.tasks.map(task => renderTask(task, {
      expanded: expandedId === task.id,
      pending: pendingId === task.id,
      canLinkSop,
      allSops,
      sopBody: task.sop_id ? sopBodyCache[task.sop_id] : null,
      isLinking: linkingTaskId === task.id,
      linkingQuery,
      onToggle: () => toggleComplete(task),
      onExpand: () => {
        const newExpanded = expandedId === task.id ? null : task.id;
        setExpandedId(newExpanded);
        if (newExpanded && task.sop_id) fetchSopBody(task.sop_id);
      },
      onDelete: () => deleteTask(task),
      onOpenLink: () => { setLinkingTaskId(task.id); setLinkingQuery(""); },
      onCancelLink: () => { setLinkingTaskId(null); setLinkingQuery(""); },
      onLinkQueryChange: setLinkingQuery,
      onPickSop: (sopId) => linkSopToTask(task.id, sopId),
      onUnlink: () => linkSopToTask(task.id, null),
      matterId, showNotification,
    })),
  );
}


function renderTask(task, opts) {
  const {
    expanded, pending, canLinkSop, allSops, sopBody,
    isLinking, linkingQuery,
    onToggle, onExpand, onDelete,
    onOpenLink, onCancelLink, onLinkQueryChange, onPickSop, onUnlink,
    matterId, showNotification,
  } = opts;
  const isDone = task.status === "completed";
  const statusColor = {
    pending: C.textMuted,
    in_progress: C.gold,
    blocked: C.error,
    completed: C.success,
    skipped: C.textLight,
  }[task.status] || C.textMuted;

  const hasSop = !!task.sop_id;
  const hasBody = !!task.body;
  const expandable = hasSop || hasBody;

  // Filter SOP list for the picker.
  const filteredSops = isLinking
    ? allSops.filter(s =>
        !linkingQuery.trim()
        || (s.title || "").toLowerCase().includes(linkingQuery.toLowerCase())
        || (s.category || "").toLowerCase().includes(linkingQuery.toLowerCase())
      ).slice(0, 30)
    : [];

  return React.createElement("div", {
    key: task.id,
    style: {
      padding: "8px 0", borderBottom: `1px solid ${C.warmGray}20`,
      opacity: isDone ? 0.6 : 1,
    },
  },
    // ─── Row: checkbox + title + status + delete ──────────────────
    React.createElement("div", {
      style: { display: "flex", alignItems: "flex-start", gap: "10px" },
    },
      React.createElement("input", {
        type: "checkbox",
        checked: isDone,
        disabled: pending,
        onChange: onToggle,
        style: { marginTop: "3px", cursor: pending ? "default" : "pointer" },
      }),
      React.createElement("div", {
        onClick: expandable ? onExpand : undefined,
        style: { flex: 1, cursor: expandable ? "pointer" : "default" },
      },
        React.createElement("div", {
          style: {
            fontSize: "13px", color: C.navy,
            textDecoration: isDone ? "line-through" : "none",
            fontWeight: 500,
          },
        }, task.title),
        React.createElement("div", {
          style: {
            fontSize: "11px", color: C.textMuted, marginTop: "2px",
            display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center",
          },
        },
          React.createElement("span", { style: { color: statusColor, fontWeight: 600 } },
            task.status.replace("_", " "),
          ),
          task.assignee_role && React.createElement("span", null, `role: ${task.assignee_role}`),
          task.assignee_firm_user_id && React.createElement("span", null,
            `user: #${task.assignee_firm_user_id}`,
          ),
          task.due_date && React.createElement("span", null,
            `due: ${task.due_date.slice(0, 10)}`,
          ),
          // Substep 5-η.5.b: auto-complete indicator. Task will tick
          // itself when the wired event fires (doc generated, audit
          // event, bankruptcy check recorded). Paralegal CAN still
          // click the checkbox manually, but they don't need to.
          task.auto_complete_trigger && React.createElement("span", {
            style: {
              padding: "1px 6px", borderRadius: "4px",
              background: `${C.success}15`, color: C.success,
              fontWeight: 600, fontSize: "10px",
            },
            title: (() => {
              const t = task.auto_complete_trigger;
              if (t.type === "doc_generated")
                return `Auto-completes when "${t.template}" is generated`;
              if (t.type === "audit_event")
                return `Auto-completes when the ${t.event_type} event fires`;
              if (t.type === "bankruptcy_check")
                return `Auto-completes when a ${t.phase} bankruptcy check is recorded`;
              return "Auto-completes when its trigger event fires";
            })(),
          }, "auto"),
          // SOP link chip (all users see it, only admin can change it)
          hasSop && React.createElement("span", {
            onClick: (e) => { e.stopPropagation(); onExpand(); },
            style: {
              display: "inline-flex", alignItems: "center", gap: "4px",
              padding: "1px 8px", borderRadius: "999px",
              background: `${C.gold}18`, color: C.textDark, fontWeight: 600,
              cursor: "pointer",
            },
            title: "Reference SOP — click to " + (expanded ? "hide" : "read"),
          }, "★ SOP: " + (task.sop_title || `#${task.sop_id}`)),
          // Link/change affordance for firm_admin+ (kept small and quiet)
          canLinkSop && React.createElement("button", {
            onClick: (e) => { e.stopPropagation(); onOpenLink(); },
            disabled: pending,
            style: {
              border: `1px dashed ${C.warmGray}`, background: "transparent",
              color: C.textMuted, cursor: pending ? "default" : "pointer",
              padding: "1px 6px", borderRadius: "4px", fontSize: "10px",
            },
            title: hasSop ? "Change linked SOP" : "Link an SOP to this task",
          }, hasSop ? "Change" : "+ Link SOP"),
          // Expand affordance — only shown when there's actually something to expand
          expandable && React.createElement("span", { style: { color: C.gold } },
            expanded ? "▲ hide" : "▼ show details",
          ),
        ),
      ),
      // Substep 5-η.5.e: per-task delete removed to prevent miss-clicks.
      // If deletion is needed, admin can nuke via direct DB or a future
      // admin console; day-to-day, unwanted tasks stay in "skipped" or
      // "completed" status.
    ),

    // ─── Substep 5-η.2: SOP-link picker (admin only, when open) ──
    isLinking && React.createElement("div", {
      style: {
        margin: "8px 0 4px 28px", padding: "12px",
        background: C.warmWhite, borderRadius: "6px",
        border: `1px solid ${C.warmGray}`,
      }
    },
      React.createElement("div", {
        style: { display: "flex", gap: "6px", marginBottom: "8px", alignItems: "center" }
      },
        React.createElement("input", {
          type: "text",
          value: linkingQuery,
          onChange: (e) => onLinkQueryChange(e.target.value),
          placeholder: "Filter SOPs by title or category…",
          autoFocus: true,
          style: {
            flex: 1, padding: "6px 10px", borderRadius: "6px",
            border: `1px solid ${C.warmGray}`, background: "#fff",
            fontSize: "12px", outline: "none",
          },
        }),
        React.createElement("button", {
          onClick: onCancelLink,
          style: {
            padding: "6px 10px", borderRadius: "6px",
            border: `1px solid ${C.warmGray}`, background: "#fff",
            color: C.textMuted, fontSize: "11px", cursor: "pointer",
          },
        }, "Cancel"),
        task.sop_id && React.createElement("button", {
          onClick: onUnlink,
          style: {
            padding: "6px 10px", borderRadius: "6px",
            border: `1px solid ${C.error}`, background: "#fff",
            color: C.error, fontSize: "11px", cursor: "pointer",
          },
          title: "Remove SOP link from this task and its source template",
        }, "Unlink"),
      ),
      React.createElement("div", {
        style: { display: "flex", flexDirection: "column", gap: "2px",
                 maxHeight: "260px", overflowY: "auto" }
      },
        filteredSops.length === 0
          ? React.createElement("div", {
              style: { padding: "10px", fontSize: "11px", color: C.textMuted, textAlign: "center" }
            }, "No SOPs match your filter.")
          : filteredSops.map(s => React.createElement("button", {
              key: s.id,
              onClick: () => onPickSop(s.id),
              style: {
                textAlign: "left", padding: "6px 10px", borderRadius: "4px",
                border: `1px solid ${task.sop_id === s.id ? C.gold : "transparent"}`,
                background: task.sop_id === s.id ? `${C.gold}18` : "#fff",
                cursor: "pointer", fontSize: "12px",
                display: "flex", justifyContent: "space-between", gap: "8px",
              },
            },
              React.createElement("span", { style: { color: C.navy, fontWeight: 500 } }, s.title),
              s.category && React.createElement("span", {
                style: { fontSize: "10px", color: C.textMuted, flexShrink: 0 }
              }, s.category),
            )),
      ),
    ),

    // ─── Expandable body — task body + linked SOP body ────────────
    expanded && React.createElement("div", { style: { marginLeft: "28px", marginTop: "8px" } },
      hasBody && React.createElement("pre", {
        style: {
          margin: 0, padding: "12px",
          background: C.warmWhite, borderRadius: "6px",
          fontSize: "12px", color: C.textDark, lineHeight: 1.5,
          whiteSpace: "pre-wrap", wordWrap: "break-word",
          fontFamily: "inherit",
          border: `1px solid ${C.warmGray}`,
        },
      }, task.body),
      hasSop && React.createElement("div", {
        style: {
          marginTop: hasBody ? "8px" : 0, padding: "12px",
          background: `${C.gold}0c`, borderRadius: "6px",
          border: `1px solid ${C.gold}55`,
        }
      },
        React.createElement("div", {
          style: {
            fontSize: "10px", color: C.textMuted, fontWeight: 600,
            letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: "8px",
            display: "flex", justifyContent: "space-between",
          }
        },
          React.createElement("span", null, "★ Firm SOP: " + (task.sop_title || `#${task.sop_id}`)),
          sopBody && sopBody.category && React.createElement("span", {
            style: { textTransform: "none", color: C.textMuted, fontWeight: 500 }
          }, sopBody.category),
        ),
        sopBody
          ? React.createElement("div", {
              style: { maxHeight: "400px", overflowY: "auto" }
            },
              React.createElement(SopBodyRenderer, {
                body: sopBody.body || "",
                matterId,
                showNotification,
              }),
            )
          : React.createElement("div", {
              style: { fontSize: "12px", color: C.textMuted, fontStyle: "italic" }
            }, "Loading SOP…"),
      ),
    ),
  );
}
